import { useEffect } from 'react';

const isValidUrl = (url) => {
  // Validate the URL to prevent XSS attacks.
  try {
    const urlObj = new URL(url);
    // Only allow http and https protocols.
    return ['http:', 'https:'].includes(urlObj.protocol);
  } catch {
    // If the URL is relative, check it does not start with a dangerous protocol.
    const trimmedUrl = url.trim().toLowerCase();
    const dangerousProtocols = ['javascript', 'data', 'vbscript'];
    return !dangerousProtocols.some((protocol) => trimmedUrl.startsWith(protocol + ':'));
  }
};

const SEOHead = ({
  title = 'Monkey Beneficios - Descubre beneficios exclusivos',
  description = 'Accede a beneficios exclusivos para tu bienestar. Descuentos en gimnasios, meditación, alimentación saludable y más.',
  image = '/logo512.png',
  url = window.location.href,
  type = 'website',
  keywords = 'beneficios, descuentos, fitness, bienestar, monkeyfit, gimnasio, meditación, alimentación saludable',
  author = 'MonkeyFit',
  twitterHandle = '@monkeyfit'
}) => {
  useEffect(() => {
    // Actualizar título
    document.title = title;

    // Función helper para actualizar o crear meta tags
    const updateMetaTag = (selector, content, attribute = 'content') => {
      let element = document.querySelector(selector);
      if (element) {
        element.setAttribute(attribute, content);
      } else {
        element = document.createElement('meta');
        const [attr, value] = selector.replace(/[[\]]/g, '').split('=');
        element.setAttribute(attr, value.replace(/"/g, ''));
        element.setAttribute(attribute, content);
        document.head.appendChild(element);
      }
    };

    // Meta tags básicos
    updateMetaTag('meta[name="description"]', description);
    updateMetaTag('meta[name="keywords"]', keywords);
    updateMetaTag('meta[name="author"]', author);

    // Open Graph
    updateMetaTag('meta[property="og:title"]', title);
    updateMetaTag('meta[property="og:description"]', description);
    updateMetaTag('meta[property="og:image"]', image);
    updateMetaTag('meta[property="og:url"]', url);
    updateMetaTag('meta[property="og:type"]', type);
    updateMetaTag('meta[property="og:site_name"]', 'Monkey Beneficios');
    updateMetaTag('meta[property="og:locale"]', 'es_ES');

    // Twitter Card
    updateMetaTag('meta[name="twitter:card"]', 'summary_large_image');
    updateMetaTag('meta[name="twitter:title"]', title);
    updateMetaTag('meta[name="twitter:description"]', description);
    updateMetaTag('meta[name="twitter:image"]', image);
    updateMetaTag('meta[name="twitter:site"]', twitterHandle);
    updateMetaTag('meta[name="twitter:creator"]', twitterHandle);

    // Meta tags adicionales
    updateMetaTag('meta[name="robots"]', 'index, follow');
    updateMetaTag('meta[name="viewport"]', 'width=device-width, initial-scale=1.0');
    updateMetaTag('meta[name="theme-color"]', '#3C52FF');
    updateMetaTag('meta[http-equiv="X-UA-Compatible"]', 'IE=edge', 'content');

    // Canonical URL
    const safeUrl = isValidUrl(url) ? url : window.location.href;
    let canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', safeUrl);
    } else {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      canonical.setAttribute('href', safeUrl);
      document.head.appendChild(canonical);
    }

    // JSON-LD Structured Data
    const structuredData = {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: 'Monkey Beneficios',
      description: description,
      url: url,
      publisher: {
        '@type': 'Organization',
        name: 'MonkeyFit',
        logo: {
          '@type': 'ImageObject',
          url: image
        }
      }
    };

    let scriptTag = document.querySelector('script[type="application/ld+json"]');
    if (scriptTag) {
      scriptTag.textContent = JSON.stringify(structuredData);
    } else {
      scriptTag = document.createElement('script');
      scriptTag.type = 'application/ld+json';
      scriptTag.textContent = JSON.stringify(structuredData);
      document.head.appendChild(scriptTag);
    }
  }, [title, description, image, url, type, keywords, author, twitterHandle]);

  return null; // Este componente no renderiza nada
};

export default SEOHead;
